import { Copy01, MarkerPin02 } from "@untitledui/icons";
import type { ReactNode } from "react";
import { AvatarLabelGroup } from "@/components/Atoms/AvatarLabelGroup/AvatarLabelGroup";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import type {
	BadgeColors,
	BadgeTypes,
	IconComponentType,
} from "@/components/base/badges/badge-types";
import { BadgeWithDot, BadgeWithIcon } from "@/components/base/badges/badges";
import { Label } from "@/components/base/label/label";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";

export const TableCellContentType = {
	PrimaryAndSecondary: "primaryAndSecondary",
	Primary: "primary",
	Secondary: "secondary",
	AvatarLabel: "avatarLabel",
	Badge: "badge",
	Label: "label",
	FeaturedIcon: "featuredIcon",
	Date: "date",
	DateAndTime: "dateAndTime",
	Address: "address",
	Phone: "phone",
	CopyableId: "copyableId",
	IconButtons: "iconButtons",
	Empty: "empty",
} as const;

export type TableCellContentType =
	(typeof TableCellContentType)[keyof typeof TableCellContentType];

export interface TableCellContentProps {
	/** Figma Table/CellContent type. */
	type?: TableCellContentType;
	/** Primary line / main value. */
	primary?: ReactNode;
	/** Secondary supporting line. */
	secondary?: ReactNode;
	/** Badge / label color when type is badge. */
	badgeColor?: BadgeColors;
	/**
	 * Badge shape/style when type is badge (`pill-color` | `color` | `modern`).
	 * Defaults to `modern` for Figma BadgeWithDot; pass `pill-color` for pill.
	 */
	badgeType?: BadgeTypes;
	/** Icon for label, featured-icon, or badge-with-icon variants. */
	icon?: IconComponentType;
	/** Avatar image for avatar-label variant. */
	avatarSrc?: string;
	/** Avatar alt text. */
	avatarAlt?: string;
	/** Icon buttons rendered for the icon-buttons type. */
	actions?: Array<{
		icon: IconComponentType;
		label: string;
		onPress?: () => void;
	}>;
	/** Called when copyable id control is pressed. */
	onCopy?: () => void;
	className?: string;
}

/**
 * Presentational cell content matching Figma Table/CellContent (3037:128).
 * Compose inside table body cells / column `cell` renderers.
 */
export function TableCellContent({
	type = TableCellContentType.Secondary,
	primary,
	secondary,
	badgeColor = "gray",
	badgeType = "modern",
	icon: Icon,
	avatarSrc,
	avatarAlt,
	actions,
	onCopy,
	className,
}: TableCellContentProps) {
	if (type === TableCellContentType.Empty) {
		return (
			<span
				className={cx("text-sm font-bold leading-5 text-quaternary", className)}
			>
				–
			</span>
		);
	}

	if (type === TableCellContentType.Primary) {
		return (
			<span
				className={cx(
					"block truncate font-display text-sm font-extrabold leading-5 text-primary",
					className,
				)}
			>
				{primary}
			</span>
		);
	}

	if (type === TableCellContentType.Secondary) {
		return (
			<span
				className={cx(
					"block min-w-0 truncate text-sm font-semibold leading-5 text-secondary",
					className,
				)}
			>
				{primary}
			</span>
		);
	}

	if (type === TableCellContentType.PrimaryAndSecondary) {
		return (
			<div
				className={cx(
					"flex min-w-0 flex-col gap-0.5 overflow-hidden",
					className,
				)}
			>
				<span className="truncate font-display text-sm font-extrabold leading-5 text-primary">
					{primary}
				</span>
				{secondary ? (
					<span className="truncate text-sm font-semibold leading-5 text-tertiary">
						{secondary}
					</span>
				) : null}
			</div>
		);
	}

	if (
		type === TableCellContentType.Date ||
		type === TableCellContentType.Phone
	) {
		return (
			<span
				className={cx(
					"whitespace-nowrap text-sm font-semibold leading-5 text-secondary",
					className,
				)}
			>
				{primary}
			</span>
		);
	}

	if (type === TableCellContentType.DateAndTime) {
		return (
			<span
				className={cx(
					"whitespace-nowrap text-sm font-semibold leading-5 text-secondary",
					className,
				)}
			>
				{primary}
			</span>
		);
	}

	if (type === TableCellContentType.Address) {
		return (
			<div className={cx("flex min-w-0 items-center gap-2", className)}>
				<MarkerPin02 className="size-5 shrink-0 text-fg-quaternary" />
				<div className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
					<span className="truncate font-display text-sm font-extrabold leading-5 text-primary">
						{primary}
					</span>
					{secondary ? (
						<span className="truncate text-sm font-semibold leading-5 text-tertiary">
							{secondary}
						</span>
					) : null}
				</div>
			</div>
		);
	}

	if (type === TableCellContentType.CopyableId) {
		return (
			<div className={cx("flex min-w-0 items-center gap-2", className)}>
				<span className="truncate text-sm font-semibold leading-5 text-secondary">
					{primary}
				</span>
				<IconButton
					hierarchy="link"
					size="sm"
					shape="round"
					icon={Copy01}
					aria-label="Copy"
					onPress={onCopy}
				/>
			</div>
		);
	}

	if (type === TableCellContentType.Badge) {
		if (Icon) {
			return (
				<BadgeWithIcon
					type={badgeType}
					color={badgeColor}
					size="md"
					iconLeading={Icon}
					className={cx("min-w-0 max-w-full gap-2", className)}
				>
					{primary}
				</BadgeWithIcon>
			);
		}
		return (
			<BadgeWithDot
				type={badgeType}
				color={badgeColor}
				size="md"
				className={className}
			>
				{primary}
			</BadgeWithDot>
		);
	}

	if (type === TableCellContentType.Label) {
		return (
			<Label
				size="md"
				type="default"
				icon={Icon}
				showIcon={Boolean(Icon)}
				className={className}
			>
				{primary}
			</Label>
		);
	}

	if (type === TableCellContentType.FeaturedIcon && Icon) {
		return (
			<FeaturedIcon
				icon={Icon}
				size="sm"
				color="gray"
				theme="light"
				className={className}
			/>
		);
	}

	if (type === TableCellContentType.AvatarLabel) {
		return (
			<AvatarLabelGroup
				size="md"
				src={avatarSrc}
				alt={avatarAlt ?? String(primary ?? "")}
				title={primary ?? ""}
				subtitle={secondary}
				className={className}
			/>
		);
	}

	if (type === TableCellContentType.IconButtons && actions?.length) {
		return (
			<div className={cx("flex items-center gap-2", className)}>
				{actions.map((action) => (
					<IconButton
						key={action.label}
						hierarchy="secondary"
						size="md"
						shape="round"
						icon={action.icon}
						aria-label={action.label}
						onPress={action.onPress}
					/>
				))}
			</div>
		);
	}

	return null;
}
